import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import EmptyState from "@/components/base/EmptyState";
import { formatDate, getMovieById, getNewsById, getRelatedNews, TYPE_LABELS } from "@/lib/media";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function NewsDetailPage() {
  const { id } = useParams();
  const news = getNewsById(id);
  const related = news ? getRelatedNews(news, 3) : [];
  const movie = news ? getMovieById(news.relatedMovieId) : undefined;

  usePageMeta({
    title: news ? `${news.title} - 华丽的外出影视动态` : "新闻不存在 - 华丽的外出",
    description: news
      ? news.excerpt
      : "该条影视动态不存在或已被移除，请返回新闻列表浏览其他内容。",
    keywords: news ? `${news.category},影视动态,${news.title},华丽的外出` : "华丽的外出,影视动态",
  });

  if (!news) {
    return (
      <SiteLayout>
        <div className="shell py-16">
          <EmptyState
            icon="ri-file-unknow-line"
            title="这条动态不存在"
            desc="它可能已经被撤下，或者链接有误。你可以回到新闻列表看看最近发生了什么。"
            actionTo="/news"
            actionLabel="返回新闻列表"
          />
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <div className="shell pt-5 md:pt-7">
        <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500" aria-label="面包屑">
          <Link to="/" className="transition-colors hover:text-primary-700">
            首页
          </Link>
          <i className="ri-arrow-right-s-line text-sm text-foreground-300" />
          <Link to="/news" className="transition-colors hover:text-primary-700">
            影视动态
          </Link>
          <i className="ri-arrow-right-s-line text-sm text-foreground-300" />
          <span className="line-clamp-1 max-w-[220px] text-foreground-800">{news.title}</span>
        </nav>

        <div className="mt-4 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
          <article className="lg:col-span-8">
            <header>
              <div className="flex flex-wrap items-center gap-2 text-[11px]">
                <span className="rounded-md bg-accent-500 px-2 py-0.5 font-bold text-foreground-950">
                  {news.category}
                </span>
                <span className="text-foreground-500">{formatDate(news.date)}</span>
                <span className="text-foreground-400">·</span>
                <span className="text-foreground-500">{news.author}</span>
                <span className="text-foreground-400">·</span>
                <span className="text-foreground-500">阅读约 {news.readTime}</span>
              </div>
              <h1 className="mt-3 text-2xl font-extrabold leading-snug tracking-tight text-foreground-950 md:text-[32px]">
                {news.title}
              </h1>
              <p className="mt-3 border-l-2 border-primary-300 pl-3 text-sm leading-relaxed text-foreground-600">
                {news.excerpt}
              </p>
            </header>

            <div className="mt-6 w-full overflow-hidden rounded-lg bg-secondary-100">
              <div className="aspect-[16/9] w-full">
                <img
                  src={news.cover}
                  alt={news.title}
                  title={`${news.title} 影视动态配图`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>

            <div className="mt-6 space-y-4">
              {news.content.map((paragraph, index) => (
                <p key={index} className="text-sm leading-[2] text-foreground-700 md:text-[15px]">
                  {paragraph}
                </p>
              ))}
            </div>

            {movie && (
              <div className="mt-8 overflow-hidden rounded-lg border border-background-200 bg-secondary-50">
                <div className="flex flex-col sm:flex-row">
                  <div className="h-40 w-full shrink-0 sm:h-auto sm:w-32">
                    <img
                      src={movie.poster}
                      alt={`《${movie.title}》海报`}
                      title={`${movie.title} 剧情介绍`}
                      className="h-full w-full object-cover object-top"
                    />
                  </div>
                  <div className="flex flex-1 flex-col justify-center p-4">
                    <p className="font-label text-[10px] font-semibold uppercase tracking-[0.18em] text-foreground-400">
                      Related Title
                    </p>
                    <h2 className="mt-1.5 text-lg font-bold text-foreground-950">《{movie.title}》</h2>
                    <p className="mt-1 text-xs text-foreground-500">
                      {TYPE_LABELS[movie.type]} · {movie.year} · {movie.region} · 评分 {movie.rating.toFixed(1)}
                    </p>
                    <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-foreground-600">{movie.tagline}</p>
                    <Link
                      to={`/detail/${movie.id}`}
                      className="mt-3 inline-flex w-fit items-center gap-1.5 rounded-md bg-primary-500 px-3.5 py-2 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
                    >
                      查看剧情与演职员
                      <i className="ri-arrow-right-line" />
                    </Link>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-8 flex flex-wrap items-center justify-between gap-3 rounded-md border border-background-200 bg-background-100/70 px-4 py-3">
              <p className="text-xs text-foreground-500">本文为虚构示例内容，仅用于展示站点结构。</p>
              <Link to="/news" className="inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:underline">
                <i className="ri-arrow-left-line" />
                返回新闻列表
              </Link>
            </div>
          </article>

          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-24">
              <h2 className="flex items-center gap-2 text-base font-bold text-foreground-950">
                <span className="h-4 w-1 rounded-full bg-accent-500" />
                相关文章
              </h2>
              <div className="mt-3.5 space-y-2.5">
                {related.map((item) => (
                  <Link
                    key={item.id}
                    to={`/news/${item.id}`}
                    className="group flex cursor-pointer gap-3 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300"
                  >
                    <div className="h-16 w-20 shrink-0 overflow-hidden rounded bg-secondary-100">
                      <img
                        src={item.cover}
                        alt={item.title}
                        title={item.title}
                        className="h-full w-full object-cover object-top"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="line-clamp-2 text-xs font-semibold leading-snug text-foreground-900 group-hover:text-primary-700">
                        {item.title}
                      </h3>
                      <p className="mt-1 text-[11px] text-foreground-400">{item.date}</p>
                    </div>
                  </Link>
                ))}
              </div>

              <div className="mt-4 rounded-lg border border-background-200 bg-background-100/70 p-4">
                <p className="text-sm font-semibold text-foreground-950">想第一时间收到动态？</p>
                <p className="mt-1 text-xs leading-relaxed text-foreground-600">
                  关注我们的更新节奏，或通过联系页面告诉我们你最关心的剧集。
                </p>
                <Link
                  to="/contact"
                  className="mt-2.5 inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:underline"
                >
                  前往联系页面
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </SiteLayout>
  );
}